import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { salaryService } from '../services/api';
import {
  Card, Button, Modal, FormField, Badge,
  Table, Pagination, EmptyState, Spinner,
} from '../components/common/UI';
import { formatCurrency, formatMonth, formatPhone } from '../utils/format';
import '../components/common/styles.css';

export default function SalaryPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [search, setSearch] = useState('');
  const [uploadModal, setUploadModal] = useState(false);
  const [detailModal, setDetailModal] = useState(null);

  const { data: months = [] } = useQuery({
    queryKey: ['salary-months'],
    queryFn: () => salaryService.months().then(r => r.data),
  });

  const { data: summary } = useQuery({
    queryKey: ['salary-summary', selectedMonth],
    queryFn: () => salaryService.summary(selectedMonth).then(r => r.data),
    enabled: true,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['salaries', page, selectedMonth, search],
    queryFn: () => salaryService.list({
      page,
      ...(selectedMonth && { month: selectedMonth }),
      ...(search && { search }),
    }).then(r => r.data),
    keepPreviousData: true,
  });

  const getVal = (data, keywords) => {
    if (!data) return 0;
    for (const [k, v] of Object.entries(data)) {
      const kl = String(k).toLowerCase();
      if (keywords.some(kw => kl.includes(kw))) {
         const parsed = parseFloat(v);
         return isNaN(parsed) ? 0 : parsed;
      }
    }
    return 0;
  };

  const columns = [
    {
      label: 'Xodim', key: 'employee_name',
      render: (row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, var(--sky-400), var(--sky-600))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: '#fff',
          }}>
            {row.employee_name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{row.employee_name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.employee_position}</div>
          </div>
        </div>
      ),
    },
    {
      label: 'Oy', key: 'month',
      render: (row) => (
        <code style={{
          fontSize: 12, background: 'var(--sky-50)',
          padding: '3px 8px', borderRadius: 4, color: 'var(--sky-700)',
        }}>{row.month}</code>
      ),
    },
    {
      label: 'Asosiy Oklad', key: 'oklad',
      render: (row) => (
        <span style={{ fontSize: 13 }}>{formatCurrency(getVal(row.data, ['oklad', 'оклад', 'tarif']))}</span>
      ),
    },
    {
      label: 'Mukofot', key: 'bonus',
      render: (row) => {
        const bonus = getVal(row.data, ['premia', 'премия', 'mukofot']);
        return (
          <span style={{
            fontSize: 13,
            color: bonus > 0 ? 'var(--emerald-500)' : 'var(--text-muted)',
            fontWeight: bonus > 0 ? 600 : 400,
          }}>
            {formatCurrency(bonus)}
          </span>
        );
      },
    },
    {
      label: 'Naqd / Qo\'lga', key: 'net',
      render: (row) => {
        let net = 0;
        if (row.data) {
           for (const [k, v] of Object.entries(row.data)) {
               const kl = String(k).toLowerCase();
               if ((['plastik', 'kartaga', 'naqd', 'ruki', 'итого на руки'].some(w => kl.includes(w))) ||
                   (['to‘lanadi', "to'lanadi", "toʻlanadi"].some(w => kl.includes(w)) && !kl.includes('to‘lanadigan') && !kl.includes("to'lanadigan"))) {
                   const parsed = parseFloat(String(v).replace(' ', '').replace(',', '.'));
                   net = isNaN(parsed) ? 0 : parsed;
                   break;
               }
           }
        }
        return (
          <span style={{
            fontSize: 14, fontWeight: 700, color: 'var(--sky-700)',
          }}>
            {formatCurrency(net)}
          </span>
        );
      },
    },
    {
      label: '', key: 'detail',
      render: (row) => (
        <Button variant="ghost" size="sm" onClick={() => setDetailModal(row)}>
          Batafsil →
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* Summary cards */}
      {summary && (
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
          {[
            { label: "Yozuvlar", value: summary?.total_records, icon: '📊', color: '#0ea5e9' },
            { label: "Jami Hisoblandi", value: formatCurrency(summary?.total_hisoblandi), icon: '💵', color: '#3b82f6' },
            { label: "Plastikka o'tkazish", value: formatCurrency(summary?.total_plastik), icon: '💳', color: '#10b981' },
          ].map(({ label, value, icon, color }, i) => (
            <div key={label} className="stat-card fade-in" style={{
              animationDelay: `${i * 60}ms`, '--stat-accent': color + '22',
            }}>
              <div className="stat-icon" style={{ background: color + '18' }}>{icon}</div>
              <div className="stat-value" style={{ fontSize: typeof value === 'string' ? 16 : 28 }}>
                {value ?? '—'}
              </div>
              <div className="stat-label">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Table card */}
      <Card>
        <div className="card-header">
          <span className="card-title">Oylik ma'lumotlari</span>
          <div className="page-actions" style={{ display: 'flex', gap: 12 }}>
            <input
              type="text"
              placeholder="Ism yoki JSHSHIR qidiring..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="form-input"
              style={{ width: 220 }}
            />
            {/* Month filter */}
            <select
              value={selectedMonth}
              onChange={e => { setSelectedMonth(e.target.value); setPage(1); }}
              className="form-input form-select"
              style={{ width: 160 }}
            >
              <option value="">Barcha oylar</option>
              {months.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>

            <Button
              variant="primary" size="md"
              icon="📤"
              onClick={() => setUploadModal(true)}
            >
              Excel yuklash
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          data={data?.results}
          loading={isLoading}
          emptyState={
            <EmptyState
              icon="💰"
              title="Oylik ma'lumotlari yo'q"
              description="Excel fayl yuklang yoki oy tanlang"
            />
          }
        />

        <Pagination
          count={data?.count || 0}
          page={page}
          onChange={setPage}
        />
      </Card>

      {/* Upload Modal */}
      <ExcelUploadModal
        isOpen={uploadModal}
        months={months}
        onClose={() => setUploadModal(false)}
        onSuccess={() => {
          qc.invalidateQueries(['salaries']);
          qc.invalidateQueries(['salary-months']);
          qc.invalidateQueries(['salary-summary']);
          qc.invalidateQueries(['dashboard-stats']);
          setUploadModal(false);
        }}
      />

      {/* Detail Modal */}
      <SalaryDetailModal
        isOpen={!!detailModal}
        salary={detailModal}
        onClose={() => setDetailModal(null)}
      />
    </div>
  );
}

function ExcelUploadModal({ isOpen, months, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState('');
  const [customMonth, setCustomMonth] = useState('');
  const fileRef = useRef();

  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('month', customMonth || month);
      return salaryService.uploadExcel(fd);
    },
    onSuccess: (res) => {
      const log = res.data;
      if (log.status === 'success') {
        toast.success(`✅ ${log.success_rows} ta yozuv saqlandi!`);
      } else if (log.status === 'partial') {
        toast(`⚠️ ${log.success_rows} ta saqlandi, ${log.error_rows} ta xato`, {
          icon: '⚠️', duration: 5000,
        });
      } else {
        toast.error(`❌ Yuklash muvaffaqiyatsiz: ${log.error_rows} xato`);
      }
      onSuccess();
      setFile(null);
      setMonth('');
      setCustomMonth('');
    },
    onError: (e) => {
      toast.error(e.response?.data?.error || "Yuklashda xatolik");
    },
  });

  const finalMonth = customMonth || month;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Excel fayl yuklash" width={480}>
      {/* Instructions */}
      <div style={{
        background: 'var(--sky-50)', border: '1px solid var(--sky-200)',
        borderRadius: 'var(--radius)', padding: '12px 16px',
        marginBottom: 20, fontSize: 13,
      }}>
        <p style={{ fontWeight: 600, color: 'var(--sky-700)', marginBottom: 6 }}>
          📋 Excel fayl formati:
        </p>
        <p style={{ color: 'var(--sky-600)', lineHeight: 1.7 }}>
          Majburiy ustun: <code style={{ background: 'var(--sky-100)', padding: '1px 5px', borderRadius: 3 }}>jshshir</code> yoki <code style={{ background: 'var(--sky-100)', padding: '1px 5px', borderRadius: 3 }}>pinfl</code>
          <br />
          Ixtiyoriy: <code style={{ background: 'var(--sky-100)', padding: '1px 5px', borderRadius: 3 }}>Оклад</code>, <code style={{ background: 'var(--sky-100)', padding: '1px 5px', borderRadius: 3 }}>Премия за подготовку поездов</code>, <code style={{ background: 'var(--sky-100)', padding: '1px 5px', borderRadius: 3 }}>Итого начислено</code> ...
        </p>
      </div>

      {/* Month selection */}
      <FormField label="Oy tanlang" required>
        <select
          value={month}
          onChange={e => setMonth(e.target.value)}
          className="form-input form-select"
          style={{ marginBottom: 8 }}
        >
          <option value="">-- Mavjud oylardan tanlang --</option>
          {months.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <input
          value={customMonth}
          onChange={e => setCustomMonth(e.target.value)}
          className="form-input"
          placeholder="Yoki yangi oy kiriting: 2024-11"
          maxLength={7}
        />
      </FormField>

      {/* File picker */}
      <FormField label="Excel fayl (.xlsx, .xls)" required>
        <div
          onClick={() => fileRef.current.click()}
          style={{
            border: `2px dashed ${file ? 'var(--sky-400)' : 'var(--border)'}`,
            borderRadius: 'var(--radius)',
            padding: '24px 16px',
            textAlign: 'center',
            cursor: 'pointer',
            background: file ? 'var(--sky-50)' : 'var(--bg-secondary)',
            transition: 'var(--transition)',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--sky-400)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = file ? 'var(--sky-400)' : 'var(--border)'}
        >
          <div style={{ fontSize: 28, marginBottom: 8 }}>{file ? '📄' : '📁'}</div>
          <p style={{ fontSize: 13, color: file ? 'var(--sky-700)' : 'var(--text-muted)', fontWeight: file ? 600 : 400 }}>
            {file ? file.name : "Faylni tanlash uchun bosing"}
          </p>
          {file && (
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {(file.size / 1024).toFixed(1)} KB
            </p>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={e => setFile(e.target.files[0])}
        />
      </FormField>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onClose}>Bekor</Button>
        <Button
          variant="primary"
          loading={uploadMutation.isPending}
          disabled={!file || !finalMonth}
          onClick={() => uploadMutation.mutate()}
          icon="📤"
        >
          Yuklash
        </Button>
      </div>
    </Modal>
  );
}

function SalaryDetailModal({ isOpen, salary, onClose }) {
  if (!salary) return null;

  const dataEntries = Object.entries(salary.data || {}).filter(([k, v]) => {
     const parsed = parseFloat(v);
     return isNaN(parsed) || parsed !== 0;
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Oylik tafsilotlari" width={600}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '65vh', overflowY: 'auto', paddingRight: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--sky-50)', borderRadius: 'var(--radius)', marginBottom: 12, border: '1px solid var(--sky-200)' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--sky-800)' }}>{salary.full_name || salary.employee_name}</div>
            <div style={{ fontSize: 13, color: 'var(--sky-600)' }}>{salary.position || salary.employee_position}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'monospace', color: 'var(--text-primary)' }}>{salary.jshshir || salary.employee_jshshir}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{salary.month}</div>
          </div>
        </div>

        <div style={{
           fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
           textTransform: 'uppercase', color: 'var(--text-muted)',
           padding: '4px 14px 4px',
        }}>
           Barcha hisob-kitoblar
        </div>

        {dataEntries.map(([k, v], idx) => {
          const isTotal = k.toLowerCase().includes('to‘lanadi') || k.toLowerCase().includes('naqd') || k.toLowerCase().includes('ruki');
          return (
            <div key={k} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 14px',
              borderBottom: idx === dataEntries.length - 1 ? 'none' : '1px solid var(--border)',
              background: isTotal ? 'var(--emerald-50)' : 'transparent',
              borderRadius: isTotal ? 'var(--radius)' : 0,
            }}>
              <span style={{ fontSize: 13, color: isTotal ? 'var(--emerald-700)' : 'var(--text-secondary)', fontWeight: isTotal ? 600 : 400 }}>{k}</span>
              <span style={{ fontSize: window.innerWidth < 600 ? 12 : 14, fontWeight: isTotal ? 700 : 500, color: isTotal ? 'var(--emerald-700)' : 'var(--text-primary)' }}>
                 {formatCurrency(v, k)}
              </span>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>Yopish</Button>
      </div>
    </Modal>
  );
}
